import {
  useState,
  useMemo,
  useRef,
  ChangeEventHandler,
  useEffect,
  FocusEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'shared/utils'
import { config } from 'config'
import { RangePickerProps } from './RangePicker.types'

const getRoundedNumber = (value: string) => {
  const float = parseFloat(value as string)
  return float ? float.toFixed(2) : null
}

export const useRangePicker = ({
  maxValue = '',
  minValue = '',
  onMaxChange,
  onMinChange,
  onClear,
  onClose,
  isOpen,
  id,
}: RangePickerProps) => {
  const maxRef = useRef<HTMLInputElement>(null)
  const escapeKeys = [config.ENTER_KEY, config.TAB_KEY]
  const numericForbiddenKeys = [config.PLUS_KEY, config.MINUS_KEY, 'e', 'E']

  const [localMin, setLocalMin] = useState(minValue)
  const [localMax, setLocalMax] = useState(maxValue)
  const { t } = useTranslation('Invoices')

  const selectValue = useMemo(() => {
    if (isEmpty(maxValue) && isEmpty(minValue)) return []
    if (!isEmpty(maxValue) && isEmpty(minValue)) return [`0 - ${maxValue}`]
    if (!isEmpty(minValue) && isEmpty(maxValue))
      return [`${minValue} - ${t('filters.amount.max')}`]
    return [`${minValue} - ${maxValue}`]
  }, [minValue, maxValue, t])

  const handleClear =
    onClear &&
    (() => {
      onClear()
      setLocalMin('')
      setLocalMax('')
    })

  const handleMinChange: ChangeEventHandler<HTMLInputElement> = e => {
    setLocalMin(e.target.value)
  }
  const handleMaxChange: ChangeEventHandler<HTMLInputElement> = e => {
    setLocalMax(e.target.value)
  }

  const onMaxBlur = (
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (
      !onMaxChange ||
      localMax === undefined ||
      (event.relatedTarget as { id?: string })?.id === `${id}-clear-btn`
    )
      return
    const rounded = getRoundedNumber(localMax as string)
    if (rounded) setLocalMax(rounded)
    onMaxChange(rounded || localMax)
  }
  const onMinBlur = (
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (
      !onMinChange ||
      localMin === undefined ||
      (event.relatedTarget as { id?: string })?.id === `${id}-clear-btn`
    )
      return
    const rounded = getRoundedNumber(localMin as string)
    if (rounded) setLocalMin(rounded)
    onMinChange(rounded || localMin)
  }

  useEffect(() => {
    if (!maxValue) {
      setLocalMax('')
    }
    if (!minValue) {
      setLocalMin('')
    }
  }, [maxValue, minValue])

  const onMinKeyDown = (event: React.KeyboardEvent) => {
    if (escapeKeys.includes(event.key)) {
      event.stopPropagation()
      event.preventDefault()
      maxRef.current?.focus()
    }
    if (numericForbiddenKeys.includes(event.key)) {
      event.preventDefault()
    }
  }

  const onMaxKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === config.ENTER_KEY) {
      event.preventDefault()
      maxRef.current?.blur()
      if (onClose) onClose()
    }
    if (numericForbiddenKeys.includes(event.key)) {
      event.preventDefault()
    }
  }

  const isError = useMemo(() => {
    const min = parseFloat(localMin as string)
    const max = parseFloat(localMax as string)
    return typeof min === 'number' && typeof max === 'number' && min > max
  }, [localMin, localMax])

  return {
    selectValue,
    minValue: localMin,
    maxValue: localMax,
    handleMinChange,
    handleMaxChange,
    onMinBlur,
    onMaxBlur,
    handleClear,
    maxRef,
    onMinKeyDown,
    onMaxKeyDown,
    isOpen,
    isError,
  }
}
